/**
 * Divisiones CPV oficiales (UE) — 2 dígitos = familia principal.
 * Lista basada en el vocabulario común de contratos públicos (Reglamento CE
 * 213/2008). Sirve para autocompletar el filtro del Buscador PLCSP sin
 * obligar al usuario a memorizar códigos.
 *
 * Cada entrada: { code, label, group }
 * - code: prefijo numérico (2-3 dígitos) que se manda como `cpv_prefix`.
 * - label: descripción legible en español.
 * - group: agrupación general (Obras/Suministros/Servicios) para colorear.
 */
export const CPV_GROUPS = [
  // — Productos / Suministros —
  { code: "03", label: "Productos agrícolas, ganadería, pesca, silvicultura", group: "Suministros" },
  { code: "09", label: "Petróleo, carbón y productos derivados", group: "Suministros" },
  { code: "14", label: "Productos mineros, metales y materiales asociados", group: "Suministros" },
  { code: "15", label: "Alimentación, bebidas, tabaco", group: "Suministros" },
  { code: "16", label: "Maquinaria agrícola", group: "Suministros" },
  { code: "18", label: "Ropa, calzado, artículos de viaje", group: "Suministros" },
  { code: "19", label: "Productos de cuero y textiles", group: "Suministros" },
  { code: "22", label: "Impresos, publicaciones y documentos", group: "Suministros" },
  { code: "24", label: "Productos químicos", group: "Suministros" },
  { code: "30", label: "Equipos de oficina e informática", group: "Suministros" },
  { code: "31", label: "Maquinaria, aparatos y equipos eléctricos", group: "Suministros" },
  { code: "32", label: "Equipos de radio, TV y telecomunicaciones", group: "Suministros" },
  { code: "33", label: "Equipos médicos, farmacéuticos y de cuidado personal", group: "Suministros" },
  { code: "34", label: "Equipos de transporte (vehículos)", group: "Suministros" },
  { code: "35", label: "Equipos de seguridad, defensa y emergencia", group: "Suministros" },
  { code: "37", label: "Instrumentos musicales, juegos y artículos deportivos", group: "Suministros" },
  { code: "38", label: "Instrumentos de medición, control y precisión", group: "Suministros" },
  { code: "39", label: "Mobiliario y productos de limpieza", group: "Suministros" },
  { code: "41", label: "Agua captada y depurada", group: "Suministros" },
  { code: "42", label: "Maquinaria industrial", group: "Suministros" },
  { code: "43", label: "Maquinaria para minería y construcción", group: "Suministros" },
  { code: "44", label: "Materiales de construcción", group: "Suministros" },
  { code: "48", label: "Paquetes de software y sistemas informáticos", group: "Suministros" },

  // — Obras —
  { code: "45", label: "Trabajos de construcción (obras)", group: "Obras" },
  { code: "4521", label: "Edificios", group: "Obras" },
  { code: "4523", label: "Carreteras, autopistas, vías", group: "Obras" },
  { code: "4525", label: "Trabajos especiales (puentes, túneles)", group: "Obras" },
  { code: "4533", label: "Instalaciones eléctricas, fontanería, climatización", group: "Obras" },
  { code: "4545", label: "Acabados de edificios", group: "Obras" },

  // — Servicios —
  { code: "50", label: "Servicios de reparación y mantenimiento", group: "Servicios" },
  { code: "51", label: "Servicios de instalación (excepto software)", group: "Servicios" },
  { code: "55", label: "Servicios de hostelería y restauración", group: "Servicios" },
  { code: "60", label: "Servicios de transporte", group: "Servicios" },
  { code: "63", label: "Servicios complementarios al transporte y logística", group: "Servicios" },
  { code: "64", label: "Servicios postales y de telecomunicaciones", group: "Servicios" },
  { code: "65", label: "Servicios públicos (agua, gas, electricidad)", group: "Servicios" },
  { code: "66", label: "Servicios financieros y de seguros", group: "Servicios" },
  { code: "70", label: "Servicios inmobiliarios", group: "Servicios" },
  { code: "71", label: "Arquitectura, ingeniería, urbanismo, inspección técnica", group: "Servicios" },
  { code: "72", label: "Servicios TI: consultoría, desarrollo, internet", group: "Servicios" },
  { code: "73", label: "Servicios de I+D y consultoría afín", group: "Servicios" },
  { code: "75", label: "Servicios de administración pública", group: "Servicios" },
  { code: "76", label: "Servicios relacionados con la industria del petróleo y gas", group: "Servicios" },
  { code: "77", label: "Servicios agrícolas, forestales y de paisajismo", group: "Servicios" },
  { code: "79", label: "Servicios a empresas: derecho, marketing, consultoría", group: "Servicios" },
  { code: "80", label: "Servicios de enseñanza y formación", group: "Servicios" },
  { code: "85", label: "Servicios sanitarios y sociales", group: "Servicios" },
  { code: "90", label: "Servicios de saneamiento, residuos y limpieza", group: "Servicios" },
  { code: "92", label: "Servicios de esparcimiento, cultura y deporte", group: "Servicios" },
  { code: "98", label: "Otros servicios comunitarios, sociales y personales", group: "Servicios" },
];

/**
 * Busca grupos CPV cuyo código empieza por `query` (numérico) o cuyo label
 * incluye `query` (textual, case-insensitive). Devuelve hasta `limit`.
 */
export function searchCpvGroups(query, limit = 8) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return CPV_GROUPS.slice(0, limit);
  const isNum = /^\d+$/.test(q);
  if (isNum) {
    return CPV_GROUPS.filter((g) => g.code.startsWith(q)).slice(0, limit);
  }
  return CPV_GROUPS.filter((g) => g.label.toLowerCase().includes(q)).slice(0, limit);
}
